import React from "react";
import { Helmet } from "react-helmet";

export default function PregnancyServices() {
  const services = [
    {
      id: "pregnancy-testing",
      title: "Pregnancy Testing",
      bgImage: "bg-pregnancytest",
      description:
        "Whether you know you're pregnant or are questioning, we offer pregnancy tests and verification of pregnancy.",
      buttonText: "Text To Make An Appointment",
      buttonLink: "sms:+18707792229",
      faqs: [
        {
          question: "How Do Pregnancy Tests Work?",
          answer:
            "All pregnancy tests detect the hormone human chorionic gonadotrophin (hCG), which starts to be produced around 6 days after conception.",
        },
        {
          question: "When Should I Take A Pregnancy Test?",
          answer:
            "You can carry out most pregnancy tests from the first day of a missed period. If you don't know when your next period is due, do the test at least 21 days after you last had sex.",
        },
        {
          question: "What If My Test Comes Back Positive?",
          answer:
            "Even if you had a positive at home test, it is still necessary to confirm you are pregnant. You should make the appointment as soon as possible to get an ultrasound. Reach out to see about your options.",
        },
      ],
    },
    {
      id: "ultrasounds",
      title: "Ultrasounds",
      bgImage: "bg-ultrasound",
      description:
        "There's so much more to know about your pregnancy, like whether you've miscarried or how many weeks you've been pregnant. Information you can only get with ultrasound.",
      buttonText: "Text To Make An Appointment",
      buttonLink: "sms:+18707792229",
      faqs: [
        {
          question: "How Does An Ultrasound Work?",
          answer:
            "An ultrasound is painless. The process uses high-frequency sound waves to create an image of the inside of your uterus. The sound waves are transmitted through a gel placed on your abdomen. Our technician rubs a device known as a transducer through the gel. The echoes of the sound waves are recorded and transformed into video or images.",
        },
        {
          question: "Why Do I Need An Ultrasound?",
          answer:
            "1) An ultrasound determines if you have a growing pregnancy, or if you might be subject to miscarrying. 2) An ultrasound can help determine how long you've been pregnant. 3) Sometimes a pregnancy can develop outside the uterus, in the Fallopian tubes. Although rare, this ectopic pregnancy can be life-threatening. An ultrasound can help determine if you face this risk.",
        },
      ],
    },
    {
      id: "counseling",
      title: "Professional Counseling",
      bgImage: "bg-counseling",
      description:
        "Compassionate professional counseling to help you navigate your pregnancy journey and make informed decisions.",
      buttonText: "Text For Availability",
      buttonLink: "sms:+18707792229",
      faqs: [],
    },
    {
      id: "parenting-classes",
      title: "Prenatal/Parenting Classes",
      bgImage: "bg-parentingclass",
      description: "Prepared Childbirth • Baby Care • Infant CPR",
      buttonText: "Text For Class Schedule",
      buttonLink: "sms:+18707792229",
      faqs: [],
    },
    {
      id: "baby-boutique",
      title: "Baby Boutique",
      bgImage: "bg-babyboutique",
      description:
        "Attending classes gives you the opportunity to earn points that can be used to 'purchase' items in the Baby Boutique so you can be ready for the big day. Clothes, diapers, wipes, toys and more are available for you.",
      buttonText: "Text For Class Schedule",
      buttonLink: "sms:+18707792229",
      faqs: [],
    },
  ];

  return (
    <div className="bg-gradient-to-b from-white via-blue-50/30 to-white font-nunito">
      <Helmet>
        <link rel="icon" href="favicon.ico" />
        <title>Pregnancy Services | Pathway Resource Center</title>
        <meta
          name="description"
          content="Discover the pregnancy support services available and how to access them for a healthy pregnancy. Get access to free services and support during pregnancy. Learn about the early pregnancy symptoms and what they mean for your pregnancy."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#457B9D] via-[#5a8dad] to-[#6ba3c4] py-20">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute right-0 top-0 h-96 w-96 rounded-full bg-white blur-3xl"></div>
          <div className="absolute bottom-0 left-0 h-96 w-96 rounded-full bg-white blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <div className="mb-6 inline-block rounded-full bg-white/20 px-6 py-2 backdrop-blur-sm">
            <span className="text-sm font-semibold uppercase tracking-wide text-white">
              Free & Confidential
            </span>
          </div>

          <h1 className="mb-6 text-5xl font-bold leading-tight text-white md:text-6xl">
            Pregnancy Services
          </h1>

          <p className="mx-auto max-w-2xl text-xl leading-relaxed text-blue-50">
            All our pregnancy services are{" "}
            <span className="font-semibold text-white">free</span> and{" "}
            <span className="font-semibold text-white">confidential</span>. We're
            here to support you every step of the way.
          </p>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg
            viewBox="0 0 1440 120"
            className="w-full"
            preserveAspectRatio="none"
            style={{ height: "80px" }}
          >
            <path
              d="M0,64 C360,120 720,0 1440,64 L1440,120 L0,120 Z"
              fill="white"
              opacity="0.3"
            />
            <path
              d="M0,80 C360,20 720,100 1440,80 L1440,120 L0,120 Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="space-y-12">
            {services.map((service, index) => (
              <div
                key={service.id}
                className="group"
                style={{
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                }}
              >
                <div className="overflow-hidden rounded-3xl bg-white shadow-lg transition-all duration-500 hover:shadow-2xl">
                  <div className="grid gap-8 lg:grid-cols-3">
                    {/* Image Section */}
                    <div className="relative h-64 overflow-hidden lg:h-auto">
                      <div
                        className={`h-full w-full ${service.bgImage} bg-cover bg-center transition-transform duration-700 group-hover:scale-110`}
                      ></div>
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-[#457B9D]/20 to-transparent"></div>
                    </div>

                    {/* Content Section */}
                    <div className="lg:col-span-2 p-8 lg:p-12">
                      {/* Title */}
                      <h2 className="mb-4 text-3xl font-bold text-gray-800 md:text-4xl">
                        {service.title}
                      </h2>

                      {/* Description */}
                      <p className="mb-6 text-lg leading-relaxed text-gray-600">
                        {service.description}
                      </p>

                      {/* CTA Button */}
                      <div className="mb-8">
                        <a
                          href={service.buttonLink}
                          className="inline-flex items-center gap-2 rounded-xl bg-[#457B9D] px-6 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:bg-[#365f7a] hover:shadow-xl"
                        >
                          {service.buttonText}
                          <svg
                            className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 8l4 4m0 0l-4 4m4-4H3"
                            />
                          </svg>
                        </a>
                      </div>

                      {/* FAQs Section */}
                      {service.faqs.length > 0 && (
                        <div className="space-y-4">
                          <h3 className="text-lg font-semibold text-gray-800">
                            Frequently Asked Questions:
                          </h3>

                          <div className="space-y-3">
                            {service.faqs.map((faq, faqIndex) => (
                              <details
                                key={faqIndex}
                                className="group/faq rounded-xl border-2 border-gray-100 bg-gray-50 transition-all duration-300 hover:border-[#457B9D]/30 hover:bg-white"
                              >
                                <summary className="flex cursor-pointer items-center justify-between p-4 font-medium text-[#457B9D] transition-colors duration-300 group-open/faq:text-pink-500">
                                  <span>{faq.question}</span>
                                  <svg
                                    className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-open/faq:rotate-180"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                </summary>
                                <div className="border-t border-gray-100 p-4 pt-3">
                                  <p className="leading-relaxed text-gray-700">
                                    {faq.answer}
                                  </p>
                                </div>
                              </details>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="bg-gradient-to-r from-blue-50 to-pink-50 py-12">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <div className="rounded-2xl bg-white p-6 shadow-md">
            <p className="text-base leading-relaxed text-gray-700">
              Pathway does not provide abortions or abortion referrals.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#457B9D] to-[#365f7a] py-16">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -left-20 top-0 h-64 w-64 rounded-full bg-white blur-3xl"></div>
          <div className="absolute -right-20 bottom-0 h-64 w-64 rounded-full bg-white blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-4xl px-6 text-center text-white">
          <h3 className="mb-4 text-3xl font-bold md:text-4xl">
            Ready to Take the Next Step?
          </h3>
          <p className="mb-8 text-xl leading-relaxed text-blue-50">
            We're here to support you. Reach out today to schedule an appointment or
            ask any questions you may have.
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
            <a
              href="sms:+18707792229"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-8 py-4 text-lg font-bold text-[#457B9D] shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              Text Us Now
            </a>
            <a
              href="tel:+18707792229"
              className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white bg-transparent px-8 py-4 text-lg font-bold text-white transition-all duration-300 hover:bg-white hover:text-[#457B9D]"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              Call 870-779-2229
            </a>
          </div>
        </div>
      </section>

      {/* Add animation keyframes */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
